import { isArray, isObject, mapValues } from 'lodash';
import { ModelEntityType } from '../model/input/types';
import { RenderFileType, RenderOptions, RenderViewComponentType } from './types';

interface CustomEncoding {
  raw: string;
  encoded: string;
}

// OA: A hack to get around encoding issues when taking screenshots with Puppeteer.
// One may be able to get rid of this hack by carefully examining how Puppeteer decodes URLs,
// and subsequently encoding the URL sent to Puppeteer so that the UI gets a valid decoded URL from Puppeteer.
const chartTitleCustomEncodings: CustomEncoding[] = [{ raw: '%', encoded: '__pct__' }];

export function getShareImageRenderOptions(
  id: string,
  type: ModelEntityType,
  version: string,
  release: string | undefined,
) {
  const options: Partial<RenderOptions> = {
    items: [{ index: 0, resultId: undefined, type, id }],
    renderFileType: RenderFileType.PNG,
    isShareImg: true,
    showControlUi: false,
    version,
    release,
    // Sharing images have to be exactly 1200 x 628
    width: 1200,
    height: 628,
    componentParams: [{ componentType: RenderViewComponentType.IMPACT_CHART, order: 0 }],
  };
  return options;
}

export function renderOptionsToQueryStringValue(_data: Partial<RenderOptions>) {
  const data = mapValuesDeep(_data, undefined, (value, key) => {
    if (typeof key === 'string') {
      if (['takeaway', 'netImpactRatio'].includes(key) && typeof value === 'string') {
        // We need to encode html symbols, as they cause a mayhem in the URL.
        return encodeURIComponent(value);
      }
      if (key === 'chartTitle') {
        return chartTitleCustomEncodings.reduce((currValue, encoding) => {
          return typeof currValue === 'string'
            ? currValue.replace(new RegExp(`${encoding.raw}`, 'g'), encoding.encoded)
            : currValue;
        }, value || '');
      }
    }
    return value;
  });
  const stringified = JSON.stringify(data);
  return encodeURIComponent(stringified);
}

export function queryStringValueToRenderOptions(data: string): Partial<RenderOptions> {
  const parsed = JSON.parse(data) as Partial<RenderOptions>;
  return mapValuesDeep(parsed, undefined, (value, key) => {
    if (typeof key === 'string') {
      if (key === 'chartTitle') {
        return chartTitleCustomEncodings.reduce((currValue, encoding) => {
          return typeof currValue === 'string'
            ? currValue.replace(new RegExp(`${encoding.encoded}`, 'g'), encoding.raw)
            : currValue;
        }, value || '');
      }
    }
    return value;
  });
}

// OA: Deep mapping of a generic object may require some complex recursive typing. The lack of said typing can lead to programmer errors.
// I put some band-aid by changing the "any" types of keys to "string | number | undefined" so that the context that provides
// the "mapperFunction" gets some protection from calling properties of 'undefined' accidentally. Feel free to improve the typings
// if you have a neat solution in your mind!

function mapValuesDeep<Input>(
  input: Input,
  keyOfInput: string | number | undefined,
  mapperFunction: <MapperInput>(
    someValueInObject: MapperInput | undefined,
    key: string | number | undefined,
  ) => unknown,
): Input {
  if (isArray(input)) {
    const mapped = input.map(valueOfArray => {
      if (isObject(valueOfArray) || isArray(valueOfArray)) {
        return mapValues(valueOfArray, (v, k) => mapValuesDeep(v, k, mapperFunction));
      }
      return mapperFunction(valueOfArray, keyOfInput);
    });
    return mapped as any as Input;
  }
  if (isObject(input)) {
    return mapValues(input, (v, k) => mapValuesDeep(v, k, mapperFunction)) as any as Input;
  }
  return mapperFunction(input as any, keyOfInput) as any as Input;
}
